import apiService from './api';

export interface StartShiftRequest {
  employeeId: number;
  machineIds?: number[];
  machineGroupIds?: number[];
}

export interface ShiftMachine {
  id: number;
  machineId?: number;
  machineGroupId?: number;
  machine?: {
    id: number;
    number: string;
  };
  machineGroup?: {
    id: number;
    name: string;
  };
  assignedAt: string;
  unassignedAt?: string;
}

export interface Shift {
  id: number;
  employeeId: number;
  startedAt: string;
  endedAt?: string;
  status: 'active' | 'completed' | 'admin_ended';
  lockoutUntil?: string;
  employee: {
    id: number;
    user: {
      id: number;
      name: string;
      email: string;
    };
  };
  machines: ShiftMachine[];
  endedByUser?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface AdminEndShiftRequest {
  newEmployeeId?: number;
  machineIds?: number[];
  machineGroupIds?: number[];
}

const shiftApi = {
  /**
   * Start employee shift
   */
  startShift: async (data: StartShiftRequest): Promise<{ shift: Shift }> => {
    const response = await apiService.post('/shift/start', data);
    return response;
  },

  /**
   * End shift
   */
  endShift: async (shiftId: number): Promise<{ shift: Shift }> => {
    const response = await apiService.post(`/shift/${shiftId}/end`, {});
    return response;
  },

  /**
   * Get active shift for employee
   */
  getActiveShift: async (employeeId: number): Promise<{ shift: Shift | null }> => {
    const response = await apiService.get(`/shift/active/${employeeId}`);
    return response;
  },

  /**
   * Get shift history
   */
  getShiftHistory: async (
    employeeId: number,
    fromDate?: string,
    toDate?: string
  ): Promise<{ shifts: Shift[] }> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const queryString = params.toString();
    const url = `/shift/history/${employeeId}${queryString ? `?${queryString}` : ''}`;
    const response = await apiService.get(url);
    return response;
  },

  /**
   * Admin override: End shift and assign new employee
   */
  adminEndShift: async (
    shiftId: number,
    data: AdminEndShiftRequest
  ): Promise<{ oldShift: Shift; newShift: Shift | null }> => {
    const response = await apiService.post(`/shift/${shiftId}/admin-end`, data);
    return response;
  },

  /**
   * Get all active shifts (admin only)
   */
  getActiveShifts: async (): Promise<{ shifts: Shift[] }> => {
    const response = await apiService.get('/shift/active');
    return response;
  },

  /**
   * Check if logout is allowed
   */
  canLogout: async (shiftId: number): Promise<{
    canLogout: boolean;
    lockoutUntil?: string;
    reason: string;
  }> => {
    const response = await apiService.get(`/shift/${shiftId}/can-logout`);
    return response;
  },

  /**
   * Mark attendance with fingerprint verification
   */
  markAttendanceWithFingerprint: async (data: {
    employeeId: number;
    machineGroupIds?: number[];
    machineIds?: number[];
    fingerprintTemplate: string;
    deviceId: number;
  }): Promise<{ shift: Shift }> => {
    const response = await apiService.post('/shift/attendance/mark', data);
    return response;
  },

  /**
   * Logoff with fingerprint verification
   */
  logoffWithFingerprint: async (
    shiftId: number,
    data: {
      fingerprintTemplate?: string;
      deviceId?: number;
      adminOverride?: boolean;
    }
  ): Promise<{ shift: Shift }> => {
    const response = await apiService.post(`/shift/${shiftId}/logoff`, data);
    return response;
  },

  /**
   * Get active attendances
   */
  getActiveAttendances: async (): Promise<{ shifts: Shift[] }> => {
    const response = await apiService.get('/shift/attendance/active');
    return response;
  }
};

export default shiftApi;

